import { useMemo, useState,useEffect } from "react";
import { api, ROUTES, imgUrl, toFormData } from "./api";
import {
  useAdmin,
  useAsync,
  Modal,
  Field,
  ImageInput,
  Pager,
  ErrorState,
  Loading,
  Empty,
  PageHead,
  money,
} from "./ui";

const PER = 10;

export default function Products() {
  const { toast, confirm } = useAdmin();
  const { data, loading, error, reload } = useAsync(() =>
    Promise.all([api.get(ROUTES.products), api.get(ROUTES.categories)]),
  );
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [stock, setStock] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // null | {} (new) | product

  const [products, categories] = data || [[], []];
  const catName = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.name])),
    [categories],
  );

  const filtered = useMemo(
    () =>
      products.filter((p) => {
        if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
        if (cat && String(p.category_id) !== cat) return false;
        if (stock === "low" && !(p.stock > 0 && p.stock <= 5)) return false;
        if (stock === "out" && p.stock !== 0) return false;
        return true;
      }),
    [products, q, cat, stock],
  );
  const rows = filtered.slice((page - 1) * PER, page * PER);

  const remove = async (p) => {
    const ok = await confirm({
      title: "Delete product",
      body: `Delete "${p.name}"? This cannot be undone. Products that appear in orders, carts or reviews may be blocked by the database.`,
    });
    if (!ok) return;
    try {
      await api.del(`${ROUTES.products}/deleteproduct/${p.id}`);
      toast("Product deleted");
      reload();
    } catch (e) {
      toast(e.message, "err");
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;

 

  return (
    <>
      <PageHead
        title="Products"
        sub={`${products.length} in your catalog`}
        actions={
          <button className="btn primary" onClick={() => setEditing({})}>
            Add product
          </button>
        }
      />

      <div className="toolbar">
        <input
          className="input"
          placeholder="Search by name"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input"
          value={cat}
          onChange={(e) => {
            setCat(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={stock}
          onChange={(e) => {
            setStock(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Any stock level</option>
          <option value="low">Low (1-5)</option>
          <option value="out">Out of stock</option>
        </select>
      </div>

      {loading ? (
        <Loading />
      ) : filtered.length === 0 ? (
        <Empty
          title={
            products.length
              ? "No products match these filters"
              : "No products yet"
          }
          body={
            products.length
              ? "Clear a filter to see more."
              : "Add your first product to start selling."
          }
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Tags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="cell-media">
                      {p.img ? (
                        <img className="thumb" src={imgUrl(p.img)} alt="" />
                      ) : (
                        <span className="thumb" />
                      )}
                      <span>{p.name}</span>
                    </div>
                  </td>
                  <td>{catName[p.category_id] || "-"}</td>
                  <td>
                    {money(p.price)}
                    {p.priceMax ? ` - ${money(p.priceMax)}` : ""}
                  </td>
                  <td>
                    {p.stock === 0 ? (
                      <span className="badge danger">Out of stock</span>
                    ) : p.stock <= 5 ? (
                      <span className="badge warn">{p.stock} left</span>
                    ) : (
                      p.stock
                    )}
                  </td>
                  <td>
                    {p.is_featured ? (
                      <span className="badge info">Featured</span>
                    ) : null}{" "}
                    {p.is_best_seller ? (
                      <span className="badge ok">Best seller</span>
                    ) : null}
                  </td>
                  <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 8,
                      }}
                    >
                      <button className="btn sm" onClick={() => setEditing(p)}>
                        Edit
                      </button>
                      <button
                        className="btn sm danger-ghost"
                        onClick={() => remove(p)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} total={filtered.length} per={PER} onPage={setPage} />

      {editing && (
        <ProductForm
          product={editing.id ? editing : null}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

const parsePacks = (v) => {
  if (!v) return [];
  try {
    const arr = typeof v === "string" ? JSON.parse(v) : v;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

function ProductForm({ product, categories, onClose, onSaved }) {
  const { toast } = useAdmin();
  const [f, setF] = useState({
    name: product?.name ?? "",
    tagline: product?.tagline ?? "",
    category_id: product?.category_id ?? "",
    price: product?.price ?? "",
    priceMax: product?.priceMax ?? "",
    stock: product?.stock ?? "",
    sold_count: product?.sold_count ?? 0,
    description: product?.description ?? "",
    Benefits: product?.Benefits ?? "",
    Ingredients: product?.Ingredients ?? "",
    isBestseller: !!product?.is_best_seller,
    isFeatured: !!product?.is_featured,
  });
  const [packs, setPacks] = useState(parsePacks(product?.packs));
  const [file, setFile] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [extraFiles, setExtraFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // packs helpers
  const addPack = () =>
    setPacks((l) => [...l, { label: "", price: "", priceMax: "" }]);
  const updatePack = (i, k, v) =>
    setPacks((l) => l.map((p, x) => (x === i ? { ...p, [k]: v } : p)));
  const removePack = (i) => setPacks((l) => l.filter((_, x) => x !== i));

  useEffect(() => {
    if (!product) return;
    api
      .get(`${ROUTES.products}/${product.id}`)
      .then((p) => setGallery(p.images || []))
      .catch(() => {});
  }, [product]);

  const removeSaved = async (g) => {
    try {
      await api.del(`${ROUTES.products}/deleteimage/${g.id}`);
      setGallery((list) => list.filter((x) => x.id !== g.id));
    } catch (err) {
      toast(err.message, "err");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!f.name.trim()) er.name = "Enter a product name";
    if (f.category_id === "") er.category_id = "Choose a category";
    if (f.price === "" || Number(f.price) < 0)
      er.price = "Enter a price of 0 or more";
    if (f.priceMax !== "" && Number(f.priceMax) < Number(f.price))
      er.priceMax = "Must be at least the price";
    if (
      f.stock !== "" &&
      (!Number.isInteger(Number(f.stock)) || Number(f.stock) < 0)
    )
      er.stock = "Use a whole number";
    if (
      f.sold_count !== "" &&
      (!Number.isInteger(Number(f.sold_count)) || Number(f.sold_count) < 0)
    )
      er.sold_count = "Use a whole number";
    if (packs.some((p) => !p.label.trim() || p.price === ""))
      er.packs = "Each pack needs a label and a price";
    setErrors(er);
    if (Object.keys(er).length) return;

    setSaving(true);
    try {
      const cleanPacks = packs.map((p) => ({
        label: p.label.trim(),
        price: Number(p.price),
        priceMax: p.priceMax === "" ? null : Number(p.priceMax),
      }));

      const form = toFormData({
        ...f,
        name: f.name.trim(),
        tagline: f.tagline.trim(),
        description: f.description.trim(),
        isBestseller: f.isBestseller ? "true" : "false",
        isFeatured: f.isFeatured ? "true" : "false",
        image: file,
      });
      form.append("packs", JSON.stringify(cleanPacks));
      extraFiles.forEach((x) => form.append("images", x));

      if (product)
        await api.upload(
          "PUT",
          `${ROUTES.products}/updateproduct/${product.id}`,
          form,
        );
      else await api.upload("POST", `${ROUTES.products}/addproduct`, form);
      toast(product ? "Product saved" : "Product added");
      onSaved();
    } catch (err) {
      toast(err.message, "err");
      setSaving(false);
    }
  };

  return (
    <Modal
      wide
      title={product ? "Edit product" : "Add product"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" form="product-form" disabled={saving}>
            {saving ? "Saving…" : product ? "Save changes" : "Add product"}
          </button>
        </>
      }
    >
      <form id="product-form" onSubmit={submit} className="form-grid" noValidate>
        <Field label="Name" error={errors.name}>
          <input className="input" value={f.name} onChange={set("name")} autoFocus />
        </Field>

        <Field label="Tagline" hint="e.g. Relief • Energy • Immunity">
          <input className="input" value={f.tagline} onChange={set("tagline")} />
        </Field>

        <Field label="Category" error={errors.category_id}>
          <select className="input" value={f.category_id} onChange={set("category_id")}>
            <option value="">Choose…</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Price (Rs)" error={errors.price}>
          <input className="input" type="number" min="0" step="0.01"
            value={f.price} onChange={set("price")} />
        </Field>

        <Field label="Highest price (Rs)" error={errors.priceMax}
          hint="Optional. Fill in to show a price range.">
          <input className="input" type="number" min="0" step="0.01"
            value={f.priceMax} onChange={set("priceMax")} />
        </Field>

        <Field label="Stock" error={errors.stock} hint="Leave empty for 0.">
          <input className="input" type="number" min="0" step="1"
            value={f.stock} onChange={set("stock")} />
        </Field>

        <Field label="Sold count" error={errors.sold_count}>
          <input className="input" type="number" min="0" step="1"
            value={f.sold_count} onChange={set("sold_count")} />
        </Field>

        <div className="span-2">
          <Field label="Description">
            <textarea className="input" rows="4" value={f.description}
              onChange={set("description")} />
          </Field>
        </div>
        <div className="span-2">
          <Field label="Benefits">
            <textarea className="input" rows="3" value={f.Benefits}
              onChange={set("Benefits")} />
          </Field>
        </div>
        <div className="span-2">
          <Field label="Ingredients">
            <textarea className="input" rows="3" value={f.Ingredients}
              onChange={set("Ingredients")} />
          </Field>
        </div>

        {/* PACKS */}
        <div className="span-2">
          <span className="field-label">Packs</span>
          {packs.map((p, i) => (
            <div key={i}
              style={{ display: "flex", gap: 8, margin: "8px 0", alignItems: "center" }}>
              <input className="input" placeholder="Label (e.g. 60 Capsules)"
                value={p.label} onChange={(e) => updatePack(i, "label", e.target.value)} />
              <input className="input" type="number" min="0" step="0.01"
                placeholder="Price" value={p.price}
                onChange={(e) => updatePack(i, "price", e.target.value)} />
              <input className="input" type="number" min="0" step="0.01"
                placeholder="Highest price (optional)" value={p.priceMax ?? ""}
                onChange={(e) => updatePack(i, "priceMax", e.target.value)} />
              <button type="button" className="btn sm danger-ghost"
                onClick={() => removePack(i)}>
                Remove
              </button>
            </div>
          ))}
          {errors.packs && <div style={{ color: "crimson", fontSize: 12 }}>{errors.packs}</div>}
          <button type="button" className="btn sm" onClick={addPack}>
            + Add pack
          </button>
        </div>

        {/* FLAGS */}
        <div className="span-2" style={{ display: "flex", gap: 24 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={f.isFeatured}
              onChange={(e) => setF({ ...f, isFeatured: e.target.checked })} />
            Featured
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={f.isBestseller}
              onChange={(e) => setF({ ...f, isBestseller: e.target.checked })} />
            Best Seller
          </label>
        </div>

        <div className="span-2">
          <span className="field-label">Image</span>
          <ImageInput current={product?.img} file={file} onChange={setFile} />
        </div>

        <div className="span-2">
          <span className="field-label">More images</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "8px 0" }}>
            {gallery.map((g) => (
              <div key={g.id} style={{ position: "relative" }}>
                <img src={imgUrl(g.url)} alt=""
                  style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6 }} />
                <button type="button" onClick={() => removeSaved(g)}
                  style={{ position: "absolute", top: -6, right: -6 }}>×</button>
              </div>
            ))}
            {extraFiles.map((x, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={URL.createObjectURL(x)} alt=""
                  style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6 }} />
                <button type="button"
                  onClick={() => setExtraFiles((l) => l.filter((_, n) => n !== i))}
                  style={{ position: "absolute", top: -6, right: -6 }}>×</button>
              </div>
            ))}
          </div>
          <input type="file" accept="image/*" multiple
            onChange={(e) => {
              const picked = Array.from(e.target.files);
              setExtraFiles((l) => [...l, ...picked]);
              e.target.value = "";
            }} />
        </div>
      </form>
    </Modal>
  );
}
